import React, { useState, useEffect } from 'react';
import './AssignDeliveryModal.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faPlus, faCheck, faTruck } from '@fortawesome/free-solid-svg-icons';
import axiosInstance from '../utils/axiosConfig';

/**
 * AssignDeliveryModal Component
 * 
 * Modal for assigning grouped recaps to a delivery.
 * Two options:
 * 1. Select an existing delivery (validates compatibility)
 * 2. Create new delivery (pre-links recaps)
 */
const AssignDeliveryModal = ({
  isOpen,
  onClose,
  groupedRecapIds = [],
  groupedRecaps = [],
  onSuccess
}) => {
  const [deliveries, setDeliveries] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedDeliveryId, setSelectedDeliveryId] = useState(null);
  const [validationError, setValidationError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');

  // Fetch deliveries when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchDeliveries();
      setSelectedDeliveryId(null);
      setValidationError('');
      setSearchTerm('');
      setDebouncedSearchTerm('');
    }
  }, [isOpen]);

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const fetchDeliveries = async () => {
    try {
      setIsLoading(true);
      // Fetch last 90 days of deliveries
      const now = new Date();
      const tomorrow = new Date(now);
      tomorrow.setDate(tomorrow.getDate() + 1);
      const endDate = tomorrow.toISOString().split('T')[0]; // Use tomorrow to include all of today
      const startDate = new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
      
      const response = await axiosInstance.get(
        `/api/deliveries/?start_date=${startDate}&end_date=${endDate}`
      );
      
      setDeliveries(response.data || []);
    } catch (error) {
      console.error('Error fetching deliveries:', error);
      setValidationError('Failed to fetch deliveries');
    } finally {
      setIsLoading(false);
    }
  };

  // Extract base product name (same logic as RecapLinkModal)
  const extractBaseProduct = (productString) => {
    if (!productString) return null;
    const parts = productString.split('|').map(p => p.trim());
    return parts[0].toLowerCase();
  };

  // Get currency from recap (supports trade and freight recaps)
  const getRecapCurrency = (recap) => {
    return recap.purchase_contract_price_currency || 
           recap.sale_contract_price_currency || 
           recap.price_currency ||
           recap.currency;
  };

  // Get unit from recap (supports trade and freight recaps)
  const getRecapUnit = (recap) => {
    return recap.purchase_contract_quantity_unit || 
           recap.sale_contract_quantity_unit || 
           recap.quantity_unit ||
           (recap.isFreightRecap && recap.quantity_mt != null ? 'MT' : undefined);
  };

  // Validate if grouped recaps are compatible with delivery's existing recaps
  const validateRecapCompatibility = (delivery) => {
    // Get delivery's existing recaps
    const existingRecaps = [
      ...(delivery.purchase_recaps_data || []),
      ...(delivery.sales_recaps_data || [])
    ];

    // If delivery has no recaps, any recaps can be added
    if (existingRecaps.length === 0) {
      return { valid: true };
    }

    // Check if grouped recaps are compatible with existing recaps
    const firstExisting = existingRecaps[0];
    const firstGrouped = groupedRecaps[0];

    if (!firstGrouped) {
      return { valid: false, error: 'No recaps in group' };
    }

    const existingProduct = extractBaseProduct(firstExisting.product);
    const groupedProduct = extractBaseProduct(firstGrouped.product);
    const existingCurrency = getRecapCurrency(firstExisting);
    const groupedCurrency = getRecapCurrency(firstGrouped);
    const existingUnit = getRecapUnit(firstExisting);
    const groupedUnit = getRecapUnit(firstGrouped);

    if (existingProduct !== groupedProduct) {
      return {
        valid: false,
        error: `Product mismatch: Delivery has "${existingProduct}", selected recaps have "${groupedProduct}"`
      };
    }

    if (existingCurrency !== groupedCurrency) {
      return {
        valid: false,
        error: `Currency mismatch: Delivery uses ${existingCurrency}, selected recaps use ${groupedCurrency}`
      };
    }

    if (existingUnit !== groupedUnit) {
      return {
        valid: false,
        error: `Unit mismatch: Delivery uses ${existingUnit}, selected recaps use ${groupedUnit}`
      };
    }

    return { valid: true };
  };

  // Handle assigning to existing delivery
  const handleAssignToExisting = async () => {
    if (!selectedDeliveryId) {
      setValidationError('Please select a delivery');
      return;
    }

    const selectedDelivery = deliveries.find(d => d.id === selectedDeliveryId);
    if (!selectedDelivery) {
      setValidationError('Delivery not found');
      return;
    }

    // Validate compatibility
    const validation = validateRecapCompatibility(selectedDelivery);
    if (!validation.valid) {
      setValidationError(validation.error);
      return;
    }

    setIsLoading(true);
    setValidationError('');

    try {
      // Link each recap to the delivery using the appropriate endpoint per
      // recap type. FREIGHT FIRST, deliberately: freight linking is
      // owner-restricted (recap creator / delivery creator / full access), so
      // a refusal must land before anything has been written — otherwise the
      // trade legs link and the user is left half-assigned with an error.
      const orderedRecaps = [...groupedRecaps].sort((a, b) => {
        const aFreight = (a.isFreightRecap || a.recap_type === 'Freight') ? 0 : 1;
        const bFreight = (b.isFreightRecap || b.recap_type === 'Freight') ? 0 : 1;
        return aFreight - bFreight;
      });
      for (const recap of orderedRecaps) {
        if (recap.isFreightRecap || recap.recap_type === 'Freight') {
          await axiosInstance.post(
            `/api/deliveries/${selectedDeliveryId}/assign_recap/`,
            { recap_transaction_number: recap.transaction_number }
          );
        } else {
          const endpoint = recap.recap_type === 'PURCHASE'
            ? `/api/deliveries/${selectedDeliveryId}/link-purchase-recap/`
            : `/api/deliveries/${selectedDeliveryId}/link-sales-recap/`;
          await axiosInstance.post(endpoint, { recap_id: recap.id });
        }
      }

      // Success - navigate to delivery
      if (onSuccess) {
        onSuccess(selectedDeliveryId);
      }
      
      onClose();
    } catch (error) {
      console.error('Error linking recaps to delivery:', error);
      setValidationError(error.response?.data?.error || 'Failed to link recaps to delivery');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle creating new delivery
  const handleCreateNew = () => {
    if (onSuccess) {
      onSuccess('new', groupedRecaps);
    }
    onClose();
  };

  // Filter deliveries based on debounced search
  const filteredDeliveries = deliveries.filter(delivery => {
    if (!debouncedSearchTerm) return true;
    
    const searchLower = debouncedSearchTerm.toLowerCase();
    return (
      delivery.delivery_unique_number?.toLowerCase().includes(searchLower) ||
      delivery.display_name?.toLowerCase().includes(searchLower) ||
      delivery.product_name?.toLowerCase().includes(searchLower) ||
      delivery.delivery_product_category?.toLowerCase().includes(searchLower)
    );
  });

  if (!isOpen) return null;

  return (
    <div className="assign-delivery-modal-overlay" onClick={onClose}>
      <div className="assign-delivery-modal" onClick={(e) => e.stopPropagation()}>
        <div className="assign-delivery-modal-header">
          <h2>Assign {groupedRecaps.length} Recaps to Delivery</h2>
          <button className="assign-delivery-modal-close" onClick={onClose}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <div className="assign-delivery-modal-content">
          {/* Option 1: Create New Delivery */}
          <div className="assign-delivery-option">
            <button 
              className="assign-delivery-new-button"
              onClick={handleCreateNew}
              disabled={isLoading}
            >
              <FontAwesomeIcon icon={faPlus} />
              <span>Create New Delivery</span>
            </button>
            <p className="assign-delivery-option-desc">
              Opens a new delivery form with these {groupedRecaps.length} recaps pre-linked
            </p>
          </div>

          <div className="assign-delivery-divider">
            <span>OR</span>
          </div>

          {/* Option 2: Select Existing Delivery */}
          <div className="assign-delivery-option">
            <h3>Select Existing Delivery</h3>
            
            {/* Search */}
            <input
              type="text"
              className="assign-delivery-search"
              placeholder="Search by delivery number, product, status..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />

            {/* Deliveries List */}
            <div className="assign-delivery-list">
              {isLoading ? (
                <div className="assign-delivery-loading">Loading deliveries...</div>
              ) : filteredDeliveries.length === 0 ? (
                <div className="assign-delivery-empty">
                  {searchTerm ? 'No deliveries match your search' : 'No deliveries found'}
                </div>
              ) : (
                filteredDeliveries.map(delivery => {
                  const recapCount = 
                    (delivery.purchase_recaps_data?.length || 0) + 
                    (delivery.sales_recaps_data?.length || 0);
                  const validation = validateRecapCompatibility(delivery);

                  return (
                    <div
                      key={delivery.id}
                      className={`assign-delivery-item ${
                        selectedDeliveryId === delivery.id ? 'selected' : ''
                      } ${!validation.valid ? 'incompatible' : ''}`}
                      onClick={() => {
                        if (validation.valid) {
                          setSelectedDeliveryId(delivery.id);
                          setValidationError('');
                        } else {
                          setValidationError(validation.error);
                        }
                      }}
                    >
                      <div className="assign-delivery-item-header">
                        <FontAwesomeIcon icon={faTruck} />
                        <strong>{delivery.display_name || 'N/A'}</strong>
                        {selectedDeliveryId === delivery.id && (
                          <FontAwesomeIcon icon={faCheck} className="assign-delivery-check" />
                        )}
                      </div>
                      <div className="assign-delivery-item-details">
                        <span>{delivery.delivery_unique_number}</span>
                        <span className="assign-delivery-recap-count">
                          {recapCount} recap{recapCount !== 1 ? 's' : ''}
                        </span>
                      </div>
                      {!validation.valid && (
                        <div className="assign-delivery-item-error">
                          Incompatible
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>

            {validationError && (
              <div className="assign-delivery-error">{validationError}</div>
            )}

            <button
              className="assign-delivery-assign-button"
              onClick={handleAssignToExisting}
              disabled={!selectedDeliveryId || isLoading}
            >
              {isLoading ? 'Assigning...' : 'Assign to Selected Delivery'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AssignDeliveryModal;
